<template>
    <Button :label="`Create ${label('Debrief')}`" class="mt-2 p-button-primary" @click="openIncidentPopup" />

    <Dialog v-model:visible="showPopup" :header="`Type of ${label('Debrief').toLowerCase()}`" :style="{ width: '100rem' }" @hide="closeIncidentPopup" modal>

        <div class="justify-content-center flex col-12 field">
            <Button :label="toggleInsertLabel" @click="toggleInsert"/>
        </div>

        <Panel :header="`Type of ${label('Debrief').toLowerCase()}`" v-if="toggleInsertMode">
            <IncidentCreateUpdateForm :popup-mode="true" @incident_selected="incidentCreated" :incidentCategoryLabel="incidentCategoryLabel"/>
        </Panel>

        <Panel header="Filters" v-if="!toggleInsertMode">
            <div class="grid grid-cols-2">
                <div class="flex flex-column col-3 field">
                    <label class="mb-2" for="incident_number">Incident Number</label>
                    <InputText id="incident_number" v-model="incident_number" type="text" placeholder="Search"/>
                </div>
                <div class="flex flex-column col-3 field">
                    <label class="mb-2" for="incident_category">Incident Category</label>
                    <Dropdown
                        id="incident_category"
                        v-model="incident_category"
                        :options="categories"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Category"
                        class="mb-1"
                        :showClear="true"
                    />
                </div>
                <div class="flex flex-column col-3 field">
                    <label for="incidentDateStart" class="mb-2">Date start:</label>
                    <Calendar
                        id="incidentDateStart"
                        class="mb-1"
                        :showIcon="true"
                        :showButtonBar="true"
                        :showOtherMonths="true"
                        v-model="incidentDateStart"
                        dateFormat="dd-mm-yy"
                    />
                </div>
                <div class="flex flex-column col-3 field">
                    <label for="incidentDateEnd" class="mb-2">Date end:</label>
                    <Calendar
                        id="incidentDateEnd"
                        class="mb-1"
                        :showIcon="true"
                        :showButtonBar="true"
                        :showOtherMonths="true"
                        v-model="incidentDateEnd"
                        dateFormat="dd-mm-yy"
                    />
                </div>
                <div class="flex col-12 justify-content-end">
                    <Button icon="pi pi-filter" label="Filter" @click="loadIncidents(1, 5)" />
                </div>
            </div>

        </Panel>
        <Divider v-if="!toggleInsertMode"/>
        <DataTable v-if="!toggleInsertMode"
            :value="incidents"
            :lazy="true"
            :paginator="true"
            :rows="5"
            dataKey="id"
            :totalRecords="totalRecords"
            :loading="loading"
            @page="onPage($event)"
            removableSort
            sortMode="multiple"
            responsiveLayout="scroll"
            paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
        >
            <template #empty> No Incidents available </template>
            <Column field="id" header="Id" />
            <Column field="incident_number" header="Incident Number" />
            <Column field="category" header="Category">
                <template #body="slotProps">
                    {{convertCategory(slotProps.data.category)}}
                </template>
            </Column>
            <Column field="incident_date" header="Incident Date" >
                <template #body="slotProps">
                    {{dateFormat(slotProps.data.incident_date)}}
                </template>
            </Column>
            <!-- new change for exercise name -->
            <Column field="exercise_name" header="Name" >
                <template #body="slotProps">
                    {{ slotProps.data.exercise_name }}
                </template>
            </Column>
            <Column header="Select" >
                <template #body="slotProps">
                    <Button icon="pi pi-check-circle" @click="selectIncident(slotProps.data)" />
                </template>
            </Column>
        </DataTable>

        <template #footer>
            <Button label="Close" severity="warning" @click="closeIncidentPopup" />
        </template>
    </Dialog>
</template>

<script setup>
import { computed, defineEmits, onMounted, ref, defineProps } from 'vue';
import { authApi } from '@/utilities/axios';
import { authStore } from '@/store/global-stores';
import { DateTime } from 'luxon';
import IncidentCreateUpdateForm from '@/product/ams/components/IncidentCreateUpdateForm.vue'
import { categories } from '@/product/ams/constants/index.js'
import { useLabels } from '@/composables/LabelComposable';

defineProps({
  incidentCategoryLabel: String
})

const emit = defineEmits(['incident_selected']);
const { label } = useLabels();

function incidentCreated(incidentCreated) {
    console.log('incidentCreated', incidentCreated)
    showPopup.value = false
    incidentCreated.data.incident_date = dateFormat(incidentCreated.data.incident_date)
    emit('incident_selected', incidentCreated.data)
}

function selectIncident(incidentData) {
    showPopup.value = false
    incidentData.incident_date = dateFormat(incidentData.incident_date)
    emit('incident_selected', incidentData)
}

const incidentDateStart = ref(null)
const incidentDateEnd = ref(null)

const showPopup = ref(false)
const loading = ref(false)

const incidents = ref([])
const totalRecords = ref(0)
const incident_number = ref(null)
const incident_category = ref(null)

const toggleInsertLabel = computed(() => {
    if (toggleInsertMode.value) {
        return 'Display List'
    } else {
        return 'Create a new one'
    }
});

const toggleInsertMode = ref(true)


const company_id = computed(() => {
    return authStore.getLoggedUserCompanyId;
});

function openIncidentPopup() {
    showPopup.value = true
}
function closeIncidentPopup() {
    showPopup.value = false
}

onMounted(async () => {
    loadIncidents(1, 5)
})

function toggleInsert() {
    toggleInsertMode.value = !toggleInsertMode.value
}

function convertCategory(category) {
    const categoryFound = categories.find((item) => parseInt(item.value) === parseInt(category));

    if (categoryFound) {
        return categoryFound.label
    } else {
        return ''
    }
}

function dateFormat(date) {

    let jsDate = DateTime.fromSQL(date)

    if (!jsDate.isValid) {
        jsDate = DateTime.fromISO(date)
    }

    if (!jsDate.isValid) {
        jsDate = DateTime.fromFormat(date, 'yyyy-MM-ddTHH:mm:ss.SSS')
    }

    if (!jsDate.isValid) {
        return ''
    } else {
        return jsDate?.setLocale('en-GB').toLocaleString({
            weekday: 'short',
            month: 'short',
            day: '2-digit',
            year: 'numeric',
        })
    }
}

function loadIncidents(requestedPage, pageSize) {
    loading.value = true

    //incident_number
    let url = `/v2/incident?company_id=${company_id.value}&page=${requestedPage}&page_size=${pageSize}&sort=id desc`

    if (incident_number.value && incident_number.value.length > 0) {
        url += `&incident_number=${incident_number.value}`
    }

    if (incident_category.value && incident_category.value > 0) {
        url += `&category=${incident_category.value}`
    }

    if (incidentDateEnd.value) {
        const dateFilter = DateTime.fromJSDate(incidentDateEnd.value)?.setLocale('en-GB').toISODate()

        url += `&incident_date_to=${dateFilter}`
    }

    if (incidentDateStart.value) {
        const dateFilter = DateTime.fromJSDate(incidentDateStart.value)?.setLocale('en-GB').toISODate()

        url += `&incident_date_from=${dateFilter}`
    }

    authApi.get(url)
        .then( async (response) => {

            if (response.statusCode === 200) {
                if (Object.hasOwn(response, 'data')) {
                    if (Object.hasOwn(response.data, 'total')) {
                        totalRecords.value = response.data.total
                    }

                    if (Object.hasOwn(response.data, 'data')) {
                        incidents.value = response.data.data
                    }
                }

            }
        }).finally(() => {
            loading.value = false
        })
}

function onPage(event) {
    loadIncidents(event.page + 1, event.rows)
}

</script>

<style scoped>

</style>